import { Injectable, inject } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { switchMap, tap, pipe, withLatestFrom, filter, takeUntil, map, distinctUntilChanged } from "rxjs";
import { QuestionnairesGridApiService } from "../services/questionnairesGrid-api-service";
import { PageResult } from "src/app/shared/models/pageResult";
import { QuestionnaireCategory, QuestionnaireCategoryCountDto } from "src/app/models/questionnaireCategory.model";
import { EQuestionnaireStatus, QuestionnaireRow } from "src/app/models/questionnaireGrid.model";
import { TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SelectedFilter } from "src/app/shared/models/filter.model";
import { ManipulationsToolbarStore } from "src/app/store/manipulations-toolbar-store";
import { GlobalService } from "src/app/shared/services/global-service";
import { GlobalStore } from "src/app/store/global-store";
import { Router } from "@angular/router";
import { EReplyStatus, Recipients } from "src/app/models/recipients.model";
import { MailForm } from "src/app/models/mailForm";
import { Reminder } from "src/app/models/reminder";
import { DistributionDetailsRequest } from "src/app/models/questionnaire.model";
import { QuestionnaireApiService } from "src/app/services/questionnaire-api.service";
import { DefinitionsStyle } from "src/app/models/definitionsStyle.model";
import { TostesStore } from "src/app/store/tostes-store";
import { TostPositionEnum, TostTypeEnum } from "src/app/shared/models/tostes.model";

export interface QuestionnairesGridState {
    questionnaires: PageResult<QuestionnaireRow>;
    questionnairesExtra: PageResult<QuestionnaireRow>;
    // questionnairesByCategory: QuestionnaireRow[][];
    categories: QuestionnaireCategoryCountDto[]
    readonly loading: boolean;
    readonly loadingExtra: boolean;
    readonly loadCategory: boolean;
    isRequestOfExtaraWasEmpty: boolean;
    currentQuestionnaire: QuestionnaireRow | null;
}
@Injectable({ providedIn: 'root' })
export class QuestionnairesGridStore extends ComponentStore<QuestionnairesGridState> {
    #translate = inject(TranslateService);
    #router = inject(Router)

    readonly questionnaires$ = this.select((x) => x.questionnaires);
    readonly questionnairesSig$ = toSignal(this.questionnaires$);
    readonly questionnairesExtra$ = this.select((x) => x.questionnairesExtra);
    readonly questionnairesExtraSig$ = toSignal(this.questionnairesExtra$);
    readonly totals$ = this.select((x) => x.questionnaires.totals);
    readonly isEmpty$ = this.select((x) => x.questionnaires.isEmpty);
    readonly categories$ = this.select((x) => x.categories);
    readonly loading$ = this.select((x) => x.loading);
    readonly loadingExtra$ = this.select((x) => x.loadingExtra);
    readonly loadingExtraSig$ = toSignal(this.loadingExtra$)
    readonly loadCategory$ = this.select((loadCategory) => loadCategory);
    readonly isRequestOfExtaraWasEmptySig$ = toSignal(this.select((x) => x.isRequestOfExtaraWasEmpty))
    readonly currentQuestionnaire$ = this.select((x) => x.currentQuestionnaire);

    #questionnairesGridApiService = inject(QuestionnairesGridApiService);
    #questionnairesApiService = inject(QuestionnaireApiService);
    #globalService = inject(GlobalService);
    #globalStore = inject(GlobalStore);
    #manipulationsToolbarStore = inject(ManipulationsToolbarStore);
    #tostesStore = inject(TostesStore);
    constructor() {
        super({
            questionnaires: { result: [], totals: 0 },
            questionnairesExtra: { result: [], totals: 0 },
            loading: false,
            loadingExtra: false,
            loadCategory: false,
            // title: { id: 0, name: 'כל השאלונים', enName: "All Questionnaires" },
            categories: [],
            isRequestOfExtaraWasEmpty: false,
            currentQuestionnaire: null
        });


        this.#manipulationsToolbarStore.selectedFilter$
            .pipe(
                distinctUntilChanged((prev, curr) => {
                    // Compare previous and current values
                    return JSON.stringify(prev) === JSON.stringify(curr);
                }),
                takeUntil(this.destroy$))
            .subscribe(x => { this.getQuestionnaires() });

        this.getQuestionnaireCategories();

        this.#globalStore.refreshQuestionnaires$.subscribe(
            x => {
                if (x != undefined) {
                    this.refreshQuestionnaire()
                }
            }
        )
    }

    readonly refreshQuestionnaire = this.effect<void>(
        pipe(tap(() => {
            this.#manipulationsToolbarStore.resetPage();
        }),
            tap(() => {
                this.patchState({ loading: true, questionnaires: { result: [], totals: 0 } })
            }),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            filter(([_, selectedFilter, page]) => selectedFilter != null),
            switchMap(([_, selectedFilter]) => {
                return this.#questionnairesGridApiService.getQuestionnaries(1, selectedFilter!).pipe()
            }
            ),
            tapResponse(
                (questionnaires) => {
                    this.patchState({ loading: false, questionnaires })
                    this.loadQuestionnariesForExtra();
                },
                (error: Error) => {
                    this.patchState({ loading: false })
                }
            )
        )
    );

    readonly getQuestionnaires = this.effect<void>(
        pipe(
            tap(() => {
                this.patchState({ loading: true, questionnaires: { result: [], totals: 0 } })
            }),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            filter(([_, selectedFilter, page]) => selectedFilter != null),
            switchMap(([_, selectedFilter, page]) => {
                return this.#questionnairesGridApiService.getQuestionnaries(page, selectedFilter!).pipe()
            }
            ),
            tapResponse(
                (questionnaires) => {
                    this.patchState({ loading: false, questionnaires })
                    this.loadQuestionnariesForExtra();
                },
                (error: Error) => {
                    this.patchState({ loading: false })
                }
            )
        )
    );

    readonly loadQuestionnariesForExtra = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loadingExtra: true })),
            tap(() => this.#manipulationsToolbarStore.updatePage()),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            filter(([_, selectedFilter, page]) => selectedFilter != null),
            switchMap(([_, selectedFilter, page]) => this.#questionnairesGridApiService.getQuestionnariesExtra(page, selectedFilter!)
            ),
            tap((updatedQuestionnairesWithTotals: PageResult<QuestionnaireRow>) => {
                this.patchState({
                    questionnairesExtra: updatedQuestionnairesWithTotals,
                })
            }),
            tap(() => this.patchState({ loadingExtra: false })),
            tap((updatedQuestionnairesWithTotals: PageResult<QuestionnaireRow>) => {
                if (this.isRequestOfExtaraWasEmptySig$()) { this.updateQuestionnaireWithExtra() }
                else {
                    this.patchState({
                        questionnairesExtra: updatedQuestionnairesWithTotals,

                    })
                }
            }),

        )
    );

    readonly resetSelectedFilterAndPaging = this.updater(
        (state) => {
            return {
                ...state,
                selectedFilter: <SelectedFilter>{},
                page: 1
            };
        }
    );

    readonly updateQuestionnaireWithExtra = this.updater((state) => (
        {
            ...state,
            isRequestOfExtaraWasEmpty: ((this.questionnairesExtraSig$()?.result == undefined || this.questionnairesExtraSig$()?.result.length == 0) && this.loadingExtraSig$()) ? true : false,
            questionnaires: {
                result: [...(this.questionnairesSig$()?.result || []), ...(this.questionnairesExtraSig$()?.result || [])],
                totals: this.questionnairesSig$()?.totals || 0
            },
            questionnairesExtra: { result: [], totals: 0 }
        }
    ));

    readonly deleteQuestionnaire = this.effect<number>(
        pipe(
            map(id => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Delete questionnaire...", he: "מוחק שאלון..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
                return { id, guid };
            }),
            switchMap((props) =>
                this.#questionnairesGridApiService.deleteQuestionnairesById(props.id).pipe(
                    tapResponse((sucsess) => {
                        this.#manipulationsToolbarStore.resetPage()
                        this.getQuestionnaires()
                        this.#tostesStore.removeTostByGuid(props.guid)
                        this.#tostesStore.addTost({ guid: "", message: { en: `The questionnaire has been successfully deleted`, he: `השאלון נמחק בהצלחה ` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                    },
                        (error) => {
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while deleting the questionnaire", he: "אירעה שגיאה בעת מחיקת השאלון" }, type: TostTypeEnum.ERROR, seconds: 5 });
                        })
                )
            )
        )
    );

    readonly duplicateQuestionnaire = this.effect<{ id: number, name?: string }>(
        pipe(
            map((props) => {
                var guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Creates a new questionnaire from a template...", he: "...יוצר שאלון חדש מתבנית" }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD })
                return { ...props, guid }
            }),
            switchMap((props) =>
                this.#questionnairesGridApiService.duplicateQuestionnairesById(props.id, props.name).pipe(
                    tapResponse((responsId) => {
                        this.#manipulationsToolbarStore.resetPage();
                        this.getQuestionnaires();
                        this.#router.navigate(['create', responsId]);

                        this.#tostesStore.removeTostByGuid(props.guid)
                        this.#tostesStore.addTost({ guid: "", message: { en: `The questionnaire ${props.name}   was created successfully`, he: `נוצר בהצלחה ${props.name} השאלון` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                    },
                        (error: Error) => {
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while creating the questionnaire from a template", he: ".אירעה שגיאה ביצירת השאלון מתבנית" }, type: TostTypeEnum.ERROR, seconds: 5 });
                        })
                )
            )
        ));

    readonly getQuestionnaireCategories = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loadCategory: true })),
            switchMap(() =>
                this.#globalService.getCategories().pipe(
                    tap(
                        (categories) => this.patchState({ categories, loadCategory: false })
                    )
                )
            )
        ));

    readonly updateTitle = this.updater((state, title: QuestionnaireCategory) => {
        return {
            ...state,
            title: title
        }
    })

    readonly updateDefinitions = this.updater((state, props: { id: number, definitions?: DefinitionsStyle, category?: QuestionnaireCategory }) => {
        return {
            ...state,
            questionnaires: {
                ...state.questionnaires, result: [...state.questionnaires.result.map(x => x.id == props.id && props.definitions ? {
                    ...x, definitions: props.definitions, name: props.definitions?.name,
                    category: props.category
                } : { ...x })]
            }
        }
    })

    readonly updateFeatures = this.updater((state, props: { id: number, apiExposure: boolean, isUnAuthorized: boolean }) => {
        return {
            ...state,
            questionnaires: {
                ...state.questionnaires, result: [...state.questionnaires.result.map(x => x.id == props.id ? {
                    ...x, definitions: { ...x.definitions, apiExposureEnabled: props.apiExposure, isUnauthorized: props.isUnAuthorized }
                } : { ...x })]
            }
        }
    })

    readonly updateTemplateImage = this.updater((state, props: { id: number, image: string }) => {
        return {
            ...state,
            questionnaires: {
                ...state.questionnaires, result: [...state.questionnaires.result.map(x => x.id == props.id && props.image ? {
                    ...x, templateImage: props.image
                } : { ...x })]
            }
        }
    })
    readonly addNewQuestionnaire = this.updater((state, newQuestionnaire: QuestionnaireRow) => {
        return {
            ...state,
            questionnaires: {
                ...state.questionnaires,
                totals: state.questionnaires.totals + 1,
                isEmpty: false,
                result: [newQuestionnaire, ...state.questionnaires.result]
            }
        }
    })

    readonly updateDistributionDetails = this.updater((state, props: { id: number, submissionDateNTZ?: Date, recipients: Recipients, isPublish: boolean, mailForm: MailForm, reminder?: Reminder, isSendAll?: boolean }) => {
        return {
            ...state,
            questionnaires: {
                ...state.questionnaires, result: [...state.questionnaires.result.map(x => x.id == props.id ? {
                    ...x, submissionDateNTZ: props.submissionDateNTZ, recipients: props.recipients,
                    distributionDate: props.isPublish ? new Date() : undefined,
                    distributionDetails: x.distributionDetails ? { ...x.distributionDetails, mailBody: props.mailForm.body, mailSubject: props.mailForm.subject, reminder: props.reminder, isSendAll: props.isSendAll } as DistributionDetailsRequest : undefined,
                    status: props.isPublish ? x.status == EQuestionnaireStatus.Edit ? EQuestionnaireStatus.Distributed : x.status : EQuestionnaireStatus.Edit
                } : { ...x })]
            }
        }
    })

    readonly updateRecipientStatus = this.updater((state, props: { questionnaireId: number, recipId: number, recipGuid: string, statusQuestionnaire: EQuestionnaireStatus }) => {
        return {
            ...state,
            questionnaires: {
                ...state.questionnaires,
                result: [...state.questionnaires.result.map(q => q.id == props.questionnaireId ? {
                    ...q, recipients: {
                        groups: [...q.recipients.groups?.map(g => { return { ...g, users: [...g.users?.map(r => r.id == props.recipId ? { ...r, statusReply: EReplyStatus.InProgress, guidToUser: props.recipGuid } : r) ?? []] } }) ?? []],
                        users: [...q.recipients.users?.map(r => { return r.id == props.recipId ? { ...r, statusReply: EReplyStatus.InProgress, guidToUser: props.recipGuid } : r }) ?? []],
                    },
                    status: props.statusQuestionnaire
                } : q)]
            }
        }
    });

    readonly saveDistributionDetails = this.effect<{ questionnaireId?: number, users: Recipients, mailForm: MailForm, reminder: Reminder, isPublish: boolean, isSendAll: boolean }>(
        pipe(
            map((props) => {
                var guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Updates distribution details...", he: "מעדכן פרטי הפצה..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD })
                return { ...props, guid }
            })
            , switchMap((props) =>

                this.#questionnairesApiService.saveDistributionDetails(
                    {
                        questionnaireId: props.questionnaireId,
                        mailSubject: props.mailForm.subject ?? "",
                        mailBody: props.mailForm.body,
                        submissionDateNTZ: props.mailForm.submissionDateNTZ,
                        reminder: props.reminder,
                        recipients: props.users,
                        isPublish: props.isPublish,
                        isAddRecipients: true,
                        sendEmail: props.mailForm.sendEmail,
                        isSendAll: props.isSendAll
                    } as DistributionDetailsRequest).pipe(
                        tapResponse(
                            (succ) => {
                                this.updateDistributionDetails({
                                    id: succ.questionnaireId!, recipients: succ.recipients,
                                    isPublish: succ.isPublish, submissionDateNTZ: succ.submissionDateNTZ, mailForm: { body: succ.mailBody, subject: succ.mailSubject } as MailForm, reminder: succ.reminder,
                                    isSendAll: succ.isSendAll
                                })

                                this.#tostesStore.removeTostByGuid(props.guid)
                                this.#tostesStore.addTost({ guid: "", message: { en: "The distribution details have been updated successfully", he: "פרטי ההפצה התעדכנו בהצלחה" }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                                if (succ.usersEmailSent?.sucsess && succ.usersEmailSent?.sucsess.length > 0) {
                                    var messageMailSucsess = " ";
                                    succ.usersEmailSent?.sucsess.forEach((mail, i) => messageMailSucsess = messageMailSucsess + (i != 0 ? " ," : "") + mail);
                                    setTimeout(() =>
                                        this.#tostesStore.addTost({ guid: "", message: { en: `Email sent to users: ${messageMailSucsess}`, he: `מייל נשלח למשתמשים :${messageMailSucsess}` }, type: TostTypeEnum.SUCCESS })
                                        , 1000)
                                }
                                if (succ.usersEmailSent?.errors && succ.usersEmailSent?.errors.length > 0) {
                                    var messageMailErrors = " ";
                                    succ.usersEmailSent?.errors.forEach((mail, i) => messageMailErrors = messageMailErrors + (i != 0 ? " ," : "") + mail);
                                    setTimeout(() =>
                                        this.#tostesStore.addTost({ guid: "", message: { en: `An error occurred while sending an email to users : ${messageMailErrors}`, he: `ארעה שגיאה בשליחת מייל למשתמשים :${messageMailErrors}` }, type: TostTypeEnum.ERROR })
                                        , 1000)
                                }
                            },
                            (error: Error) => {
                                this.#tostesStore.removeTostByGuid(props.guid)
                                this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred updating the distribution details", he: "אירעה שגיאה בעדכון פרטי ההפצה" }, type: TostTypeEnum.ERROR, seconds: 5 });
                            }
                        )
                    )
            )
        )
    );

    readonly updateMainAnswer = this.updater((state, props: { questionnaireGuid: string, mainAnswer?: string }) => {
        return {
            ...state,
            questionnaires: {
                ...state.questionnaires,
                result: [...state.questionnaires.result.map(q => q.guidToUser == props.questionnaireGuid ? {
                    ...q, mainAnswer: props.mainAnswer
                } : q)]
            }
        }
    })

    readonly cancelRequest = this.effect<{ guid: string }>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(({ guid }) =>
                this.#questionnairesGridApiService.cancelRequest(guid).pipe(
                    tapResponse(
                        () => {
                            this.removeCancelledRequest(guid);
                            this.patchState({ loading: false })
                        },
                        (error) => {
                            console.error(error);
                            this.patchState({ loading: false });
                        }
                    )
                )
            )
        )
    );
    readonly removeCancelledRequest = this.updater((state, guid: string) => ({
        ...state,
        questionnaires: {
            ...state.questionnaires,
            totals: Math.max((state.questionnaires.totals ?? 0) - 1, 0),
            result: state.questionnaires.result.filter(q => q.guidToUser !== guid)
        },
        questionnairesExtra: {
            ...state.questionnairesExtra,
            result: state.questionnairesExtra.result.filter(q => q.guidToUser !== guid)
        }
    }));

    readonly duplicateRequest = this.effect<{ guid: string }>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(({ guid }) =>
                this.#questionnairesGridApiService.duplicateRequest(guid).pipe(
                    tapResponse(
                        (newGuid) => {
                            this.patchState({ loading: false })
                            this.#globalStore.refreshQuestionnaireAfterUpdate()
                            this.#router.navigate(['answer', `${newGuid}`])
                        },
                        (error) => {
                            console.error(error);
                            this.patchState({ loading: false });
                        }
                    )
                )
            )
        )
    );

    readonly updateCurrentQuestionnaire = this.updater((state, props: { questionnaire: QuestionnaireRow | null }) => {
        return {
            ...state,
            currentQuestionnaire: props.questionnaire
        }
    });
}